import { useEffect, useId, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { filterDishes, normalizeSearch } from './surveyLogic';
import type { SurveyDish } from './types';

type Dish = { product_uuid: string | null; name: string };

interface Props {
  labelId: string;
  label: string;
  multiple: boolean;
  dishes: SurveyDish[];
  value: Dish[];
  /** Name typed and not yet turned into a tag; owned by the page so it is never lost. */
  pending: string;
  onChange: (dishes: Dish[], pending: string) => void;
  t: TFunction;
}

const MAX_DISHES = 30;
const tag = 'border-2 border-solid border-ink rounded-btn-sm';

/** SC-1752 — free text + local suggestions (case and accent insensitive), ARIA combobox. */
export default function DishPicker({ labelId, label, multiple, dishes, value, pending, onChange, t }: Props) {
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const typed = pending.trim();
  const chosen = new Set(value.map((d) => normalizeSearch(d.name)));
  const suggestions = filterDishes(dishes, pending).filter((d) => !chosen.has(normalizeSearch(d.name)));
  const exact = dishes.some((d) => normalizeSearch(d.name) === normalizeSearch(typed));
  const showManual = typed !== '' && !exact && !chosen.has(normalizeSearch(typed));
  const items: { key: string; text: string; dish: Dish }[] = [
    ...suggestions.map((d) => ({ key: d.uuid, text: d.name, dish: { product_uuid: d.uuid, name: d.name } })),
    ...(showManual
      ? [{ key: '__manual', text: t('survey.dishAdd', { name: typed }), dish: { product_uuid: null, name: typed } }]
      : []),
  ];
  const open = items.length > 0 && !dismissed;
  const optionId = (i: number) => `${listId}-o${i}`;

  // The results sit under the input: when the fixed action bar would cover them,
  // lift the input to the top of the visible area so the whole list shows.
  useEffect(() => {
    const list = listRef.current;
    if (!open || !list) return;
    const reserved = parseFloat(document.documentElement.style.scrollPaddingBottom) || 0;
    if (list.getBoundingClientRect().bottom > window.innerHeight - reserved) {
      inputRef.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
    }
  }, [open, items.length]);

  const add = (dish: Dish) => {
    if (chosen.has(normalizeSearch(dish.name))) {
      onChange(value, '');
      return;
    }
    if (multiple) onChange(value.length < MAX_DISHES ? [...value, dish] : value, '');
    else onChange([dish], '');
    setActive(-1);
  };
  const addTyped = () => {
    const match = dishes.find((d) => normalizeSearch(d.name) === normalizeSearch(typed));
    add(match ? { product_uuid: match.uuid, name: match.name } : { product_uuid: null, name: typed });
  };

  return (
    <div>
      {value.length > 0 && (
        <ul aria-label={label} className="flex flex-wrap gap-2 mb-2 p-0 list-none">
          {value.map((d) => (
            <li key={`${d.product_uuid ?? ''}:${d.name}`} className={`flex items-center gap-1 ${tag} bg-paper ps-3 font-mono`}>
              <span>{d.name}</span>
              <button
                type="button"
                aria-label={t('survey.dishRemove', { name: d.name })}
                onClick={() => onChange(value.filter((x) => x !== d), pending)}
                className="min-h-[44px] min-w-[44px] border-0 bg-transparent text-ink text-xl leading-none"
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-labelledby={labelId}
        value={pending}
        maxLength={120}
        placeholder={t('survey.dishSearch')}
        onChange={(e) => {
          onChange(value, e.target.value);
          setActive(-1);
          setDismissed(false);
        }}
        onBlur={() => {
          if (typed) addTyped();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && items.length) {
            e.preventDefault();
            setDismissed(false);
            setActive((i) => (i + 1) % items.length);
          } else if (e.key === 'ArrowUp' && items.length) {
            e.preventDefault();
            setDismissed(false);
            setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            setDismissed(true);
            setActive(-1);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && active >= 0) add(items[active].dish);
            else if (typed) addTyped();
          }
        }}
        className="w-full min-h-[44px] box-border border-2 border-solid border-ink rounded-btn-sm bg-paper text-ink px-3 font-mono text-base"
      />
      <div
        id={listId}
        ref={listRef}
        role="listbox"
        aria-label={label}
        className={open ? `mt-1 ${tag} bg-paper` : 'hidden'}
      >
        {items.map((it, i) => (
          <div
            key={it.key}
            id={optionId(i)}
            role="option"
            aria-selected={i === active}
            // keeps focus in the input, so the blur-commit does not race the tap
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => add(it.dish)}
            className={`flex items-center min-h-[44px] px-3 font-mono text-ink cursor-pointer ${
              i === active ? 'bg-ink/10 font-semibold' : ''
            }`}
          >
            {it.text}
          </div>
        ))}
      </div>
    </div>
  );
}
